import type { ScientificObjectRef } from '../../../scientific-objects/ScientificObjectRef';
import type { ScientificObjectSummary } from '../../../scientific-objects/ScientificObjectSummary';
import type { Variant } from './Variant';
import { getVariant } from './variantApi';
import { useAsyncResource } from '../../../hooks/useAsyncResource';

interface Props {
  readonly reference: ScientificObjectRef;
  readonly summary: ScientificObjectSummary;
}

const REPRESENTATION_LABELS = { HGVS_GENOMIC: 'Genomic HGVS', VCF: 'VCF' } as const;
const IDENTIFIER_LABELS = { CLINVAR: 'ClinVar', DBSNP: 'dbSNP' } as const;

export function VariantWorkspace({ reference, summary }: Props) {
  const state = useAsyncResource(() => getVariant(reference.id), [reference.id], 'Unable to load this variant.');

  return (
    <main className="variant-workspace" data-object-id={reference.id}>
      <header className="variant-heading">
        <p className="eyebrow">Variant</p>
        <h1>{summary.displayName}</h1>
        <p className="variant-id">{reference.id}</p>
      </header>
      {state.status === 'loading' ? <p>Loading variant…</p> : null}
      {state.status === 'error' ? <p role="alert">{state.message}</p> : null}
      {state.status === 'ready' ? <VariantDetails variant={state.value} /> : null}
    </main>
  );
}

function VariantDetails({ variant }: { readonly variant: Variant }) {
  const allele = variant.sequenceAllele;
  return (
    <div className="variant-details">
      <section aria-labelledby="variant-location">
        <h2 id="variant-location">Normalized sequence allele</h2>
        <dl className="variant-facts">
          <div>
            <dt>Assembly</dt>
            <dd>
              {allele.assembly.displayName} <small>{allele.assembly.identifier}</small>
            </dd>
          </div>
          <div>
            <dt>Reference sequence</dt>
            <dd>
              {allele.referenceSequence.displayName} <small>{allele.referenceSequence.identifier}</small>
            </dd>
          </div>
          <div>
            <dt>Location</dt>
            <dd>
              {allele.start}–{allele.end} <small>0-based, half-open [start, end)</small>
            </dd>
          </div>
          <div>
            <dt>Reference allele</dt>
            <dd>{allele.referenceAllele || '∅'}</dd>
          </div>
          <div>
            <dt>Alternate allele</dt>
            <dd>{allele.alternateAllele || '∅'}</dd>
          </div>
        </dl>
      </section>
      <VariantValues
        title="Representations"
        empty="No representations recorded."
        values={variant.representations.map((value) => ({
          label: REPRESENTATION_LABELS[value.type],
          value: value.value
        }))}
      />
      <VariantValues
        title="External identifiers"
        empty="No external identifiers recorded."
        values={variant.externalIdentifiers.map((value) => ({
          label: IDENTIFIER_LABELS[value.namespace],
          value: value.value
        }))}
      />
    </div>
  );
}

function VariantValues({
  title,
  empty,
  values
}: {
  readonly title: string;
  readonly empty: string;
  readonly values: readonly { label: string; value: string }[];
}) {
  const id = `variant-${title.toLowerCase().replaceAll(' ', '-')}`;
  return (
    <section aria-labelledby={id}>
      <h2 id={id}>{title}</h2>
      {values.length === 0 ? (
        <p>{empty}</p>
      ) : (
        <dl className="variant-facts">
          {values.map((value) => (
            <div key={`${value.label}:${value.value}`}>
              <dt>{value.label}</dt>
              <dd>{value.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}
